import React, { useState } from 'react';
import { Button, Input, message, Popconfirm, Space, Table, TableColumnsType, Tag, Modal, Badge } from 'antd';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import {
  getMcpServerList,
  getMcpServerTools,
  deleteMcpServerConfig,
  syncMcpServerTools,
  McpServerConfigVo,
  ToolRegistryVo,
  SyncResult,
} from '@/api/mcpServer';
import { useUpdate } from '@/common/hook';
import EditMcpServerModal from './editModal';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';

const columns: TableColumnsType<McpServerConfigVo> = [
  { title: '服务名称', dataIndex: 'serverName', key: 'serverName', width: 150 },
  // 显示名称列已隐藏（通常与服务名称重复）
  { title: '描述', dataIndex: 'description', key: 'description', width: 200, ellipsis: true },
  { title: '服务地址', dataIndex: 'serverUrl', key: 'serverUrl', ellipsis: true, width: 200 },
  // 状态列已隐藏（禁用后 Skill 无法调用工具，功能意义不大）
  {
    title: '在线',
    dataIndex: 'online',
    key: 'online',
    width: 80,
    render: (online: boolean) => (
      <Tag color={online ? 'blue' : 'default'}>{online ? '在线' : '离线'}</Tag>
    ),
  },
  {
    title: '工具数量',
    dataIndex: 'toolCount',
    key: 'toolCount',
    width: 80,
    render: (count: number) => count || 0,
  },
  {
    title: '同步状态',
    dataIndex: 'syncStatus',
    key: 'syncStatus',
    width: 100,
    render: (status: string) => {
      if (status === 'success' || status === 'connected') return <Tag color="green">成功</Tag>;
      if (status === 'failed') return <Tag color="red">失败</Tag>;
      if (status === 'reconnecting') return <Tag color="orange">重连中</Tag>;
      return <Tag>未同步</Tag>;
    },
  },
  {
    title: '最后同步时间',
    dataIndex: 'lastSyncAt',
    key: 'lastSyncAt',
    width: 160,
    render: (text: string) => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
  },
  // 同步错误列已隐藏（通常为空，占用空间）
  {
    title: '创建时间',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 160,
    render: text => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
  },
];

const McpServerList = () => {
  const [editData, setEditData] = useState<McpServerConfigVo>();
  const [editVisible, setEditVisible] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>('');
  const [update, updateFn] = useUpdate();

  // 同步结果弹窗
  const [syncResult, setSyncResult] = useState<SyncResult>();
  const [syncVisible, setSyncVisible] = useState<boolean>(false);

  // 工具列表弹窗
  const [toolsVisible, setToolsVisible] = useState<boolean>(false);
  const [currentTools, setCurrentTools] = useState<ToolRegistryVo[]>([]);
  const [toolsLoading, setToolsLoading] = useState<boolean>(false);

  const { data: serverList, loading } = useRequest(() => getMcpServerList(), {
    refreshDeps: [update],
  });

  // 查看工具列表
  const handleViewTools = (serverId: number) => {
    setToolsLoading(true);
    setToolsVisible(true);
    getMcpServerTools(serverId)
      .then(tools => {
        setCurrentTools(tools || []);
      })
      .finally(() => setToolsLoading(false));
  };

  const onDelete = (id: number) => {
    deleteMcpServerConfig(id).then(() => {
      message.success('删除成功');
      updateFn();
    });
  };

  const handleSync = (id: number) => {
    syncMcpServerTools(id).then(res => {
      setSyncResult(res);
      setSyncVisible(true);
      updateFn();
    });
  };

  const buildColumns = [
    ...columns,
    {
      title: '操作',
      key: 'operation',
      width: 180,
      render: (_: any, record: McpServerConfigVo) => (
        <Space size={4}>
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => handleViewTools(record.id)}
          >
            工具
          </Button>
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => {
              setEditData(record);
              setEditVisible(true);
            }}
          >
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => handleSync(record.id)}
          >
            同步
          </Button>
          <Popconfirm title="确定删除该 MCP Server？" onConfirm={() => onDelete(record.id)}>
            <Button type="link" size="small" style={{ padding: 0 }} danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filteredList = serverList?.filter(
    s => s.serverName.includes(keyword) || s.displayName?.includes(keyword) || s.description?.includes(keyword)
  );

  return (
    <>
      <div className="flex gap-3 mb-3">
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditVisible(true)}>
          新增 MCP Server
        </Button>
        <Input
          placeholder="搜索服务名称/显示名称"
          prefix={<SearchOutlined />}
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
      </div>
      <Table<McpServerConfigVo>
        columns={buildColumns}
        dataSource={filteredList ?? []}
        loading={loading}
        rowKey="id"
      />
      {editVisible && (
        <EditMcpServerModal
          data={editData}
          isEdit={!!editData}
          updateFn={updateFn}
          onCancel={() => {
            setEditVisible(false);
            setEditData(undefined);
          }}
        />
      )}
      {syncVisible && (
        <Modal
          title="工具同步结果"
          open={true}
          onCancel={() => setSyncVisible(false)}
          footer={<Button onClick={() => setSyncVisible(false)}>关闭</Button>}
        >
          <div>
            <p><strong>状态:</strong> {syncResult?.success ? '成功' : '失败'}</p>
            {syncResult?.toolCount && <p><strong>同步工具数量:</strong> {syncResult.toolCount}</p>}
            {syncResult?.message && <p><strong>消息:</strong> {syncResult.message}</p>}
            {syncResult?.error && <p style={{ color: 'red' }}><strong>错误:</strong> {syncResult.error}</p>}
          </div>
        </Modal>
      )}
      {toolsVisible && (
        <Modal
          title="工具列表"
          open={true}
          onCancel={() => setToolsVisible(false)}
          footer={<Button onClick={() => setToolsVisible(false)}>关闭</Button>}
          width={1000}
        >
          <Table<ToolRegistryVo>
            columns={[
              { title: '工具名称', dataIndex: 'toolName', key: 'toolName', width: 250 },
              { title: '显示名称', dataIndex: 'displayName', key: 'displayName', width: 180 },
              { title: '描述', dataIndex: 'description', key: 'description', ellipsis: true },
              { title: '分类', dataIndex: 'category', key: 'category', width: 120 },
              {
                title: '状态',
                dataIndex: 'status',
                key: 'status',
                width: 80,
                render: (status: number) => <Badge status={status === 1 ? 'success' : 'error'} text={status === 1 ? '正常' : '禁用'} />,
              },
            ]}
            dataSource={currentTools}
            loading={toolsLoading}
            rowKey="id"
            size="small"
            pagination={currentTools.length > 10 ? { pageSize: 10 } : false}
          />
        </Modal>
      )}
    </>
  );
};

export default McpServerList;